<template>
    <v-app>
        <header-bar
            ref="headerBar"
            v-model="drawer"
            :extension="
                (!isHideSelectedSnackbars &&
                    (!!selectedOrderId || !!selectedCompanyAndAgent || !!selectedPrivateClient)) ||
                $breakpoint.smAndDown
            "
        >
            <template v-slot:alerts>
                <template v-if="!isHideSelectedSnackbars">
                    <client-only>
                        <add-service-snackbar v-if="selectedOrderId" />
                        <agent-search-snackbar v-if="selectedCompanyAndAgent" />
                        <private-client-search-snackbar v-if="selectedPrivateClient" />
                    </client-only>
                </template>
            </template>
        </header-bar>
        <account-balance v-if="showAccountBalance" />
        <navigation-drawer-mobile v-if="$breakpoint.smAndDown" v-model="drawer" />
        <v-main
            :class="{
                content: $route.meta.unwrapped,
                'pb-0': ['authorization', 'b2b-auth'].includes($route.name as string) && $route.meta.unwrapped,
            }"
        >
            <notifications-snackbar />
            <notification-modal />
            <authorization-modal
                v-model="authModal"
                @auth-b2b-success="onAuthB2bSuccess"
                @auth-b2c-success="onAuthB2cSuccess"
            />
            <recent-views v-if="!isMobile && !isGuest" class="pt-1" />
            <travel-preferences v-if="isShowTravelPreferences" />
            <v-container fluid :class="{'pa-0': $route.meta.unwrapped, 'h-100': $route.meta.unwrapped}">
                <slot />
            </v-container>
        </v-main>
        <footer-bar />
        <cookies-alert v-if="showCookiesAlert && $conf.public.showCookiesAlert" @close-dialog="closeCookieAlert" />
        <div v-if="script" v-html="script" />
    </v-app>
</template>

<script lang="ts">
    import {Component, Setup} from 'vue-facing-decorator'
    import AddServiceSnackbar from '~/components/snippets/AddServiceSnackbar.vue'
    import {EventBus, LOGOUT_REDIRECT_EVENT, SHOW_AUTH_MODAL} from '@/utils/event-bus'
    import HeaderBar from '~src/components/headerBar.src'
    import FooterBar from '~src/components/footerBar.src'
    import {BaseLayout} from '@/components/BaseLayout.vue'
    import AgentSearchSnackbar from '@/components/snippets/AgentSearchSnackbar.vue'
    import NavigationDrawerMobile from '~src/components/snippets/navigationDrawerMobile.src'
    import NotificationsSnackbar from '@/components/NotificationsSnackbar.vue'
    import CookiesAlert from '~src/components/CookiesAlert.vue'
    import NotificationModal from '@/components/modals/NotificationModal.vue'
    import PrivateClientSearchSnackbar from '@/components/snippets/PrivateClientSearchSnackbar.vue'
    import {useGoTo} from 'vuetify'
    import AccountBalance from '@/components/account/AccountBalance.vue'
    import RecentViews from '~src/components/RecentViews.vue'
    import {TravelPreferences} from '~/components/TravelPreferences.vue'
    import AuthorizationModal from '@/components/account/modals/auth/AuthorizationModal.vue'

    @Component({
        components: {
            PrivateClientSearchSnackbar,
            NotificationModal,
            CookiesAlert,
            NotificationsSnackbar,
            NavigationDrawerMobile,
            AgentSearchSnackbar,
            HeaderBar,
            FooterBar,
            AddServiceSnackbar,
            AccountBalance,
            RecentViews,
            TravelPreferences,
            AuthorizationModal,
        },
    })
    export class DefaultLayout extends BaseLayout {
        @Setup(() => useGoTo()) goTo

        authModal = false

        created() {
            useNuxtApp().vueApp.provide('goTo', this.goTo)
        }

        async mounted() {
            EventBus.$on(LOGOUT_REDIRECT_EVENT, () => {
                if (this.$toast) {
                    this.$toast.error(this.$t('authorization_timeout'))
                }
            })
            EventBus.$on(SHOW_AUTH_MODAL, () => {
                this.authModal = true
            })
            useNuxtApp().vueApp.provide('layoutRefs', this.$refs)
            this.restored = !!(await this.$store.restored)
        }

        closeCookieAlert() {
            persistentStore.SET_COOKIE_ALERT_SHOWN(true)
        }

        onAuthB2cSuccess() {
            const companyType = authStore.companyType

            if (companyType === 'INTERNAL_SUPPLIER' && this.$route.name === 'home') {
                this.$router.push({name: 'supplier'})
            }

            this.authModal = false
        }

        onAuthB2bSuccess() {
            if (this.isDriverOrGuide) {
                this.$router.push({name: 'scheduler'})
            }
            const companyType = authStore.companyType

            if (companyType === 'INTERNAL_SUPPLIER' && this.$route.name === 'home') {
                this.$router.push({name: 'supplier'})
            }

            this.authModal = false
        }

        get isDriverOrGuide() {
            return authStore.isGuide || authStore.isDriver
        }

        get script() {
            return cmsStore.layoutSettings.script
        }

        get selectedOrderId() {
            return persistentStore.selectedOrderId
        }

        get selectedCompanyAndAgent() {
            return persistentStore.selectedCompanyAndAgent
        }

        get isHideSelectedSnackbars() {
            return (
                this.$route.name === 'order-details' ||
                this.$route.name === 'company-client-profile' ||
                this.$route.name === 'companyClient'
            )
        }

        get selectedPrivateClient() {
            return persistentStore.selectedPrivateClient
        }

        get showCookiesAlert() {
            return this.restored ? !persistentStore.cookieAlertShown : false
        }

        get showAccountBalance() {
            return (
                (this.isAgency || this.isCorporate) && ['director', 'supervisor', 'support'].includes(this.personRole)
            )
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isCorporate() {
            return authStore.isCorporate
        }

        get personRole() {
            return authStore.personRole
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get isGuest() {
            return authStore.isGuest
        }

        get isShowTravelPreferences(): boolean {
            const availableRoutes: string[] = ['orders', 'home']
            const route: string = this.$route.name.toString()
            return (
                (authStore.isGuest || authStore.isB2C || !authStore.isB2B) &&
                authStore.person?.travelPreferences &&
                availableRoutes.includes(route)
            )
        }
    }

    export default DefaultLayout
</script>
